// =============================================================================
// Finance cockpit — React port of the Invoice Program walkthrough (Aug 05).
//
// Tabs: Batches (active) · Posting Queue · Unbilled Shipments · All Batches·30d,
// with plan sub-tabs (N30/BK/PoT/CTO) on the batch views. Batch rows open the
// batch review screen (/finance/batch/{batchCode}); rows with an Epicor group
// also offer 📄 Edit List and Post group.
//
// THE GL POST FLOW (spec §1.9) lives here:
//   1. POST groups/{id}/post?validateOnly=true  — safe preview, never touches GL
//   2. self-heal: "already posted / being posted" → jump straight to reconcile
//   3. confirm dialog quoting the REAL $ amount + the debits=credits warning
//   4. blocking overlay → real POST (no validateOnly)
//   5. Epicor posts ASYNC: wait ~8s, then poll batches/{id}/reconcile every 4s
//      (max 30 ≈ 2 min) until allPosted / batchStatus === "Posted"
//   6. success modal listing the posted invoices; timeout → hourly-reconcile note
// Edit List: GET groups/{id}/edit-list → PDF blob; 202 = still generating (retry).
// =============================================================================
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Badge, Button, Form, Modal, Spinner, Table } from "react-bootstrap";
import { FaFileDownload, FaFileInvoiceDollar, FaFolderOpen, FaSync, FaHeartbeat } from "react-icons/fa";
import { apiFetch, hasAction, isShuklaIt } from "../utils/auth";
import { AGING_TONE, CATS, STATUS_PILL, emptyForCat, fmtDate, money } from "./financeShared";
import "../pagecss/Webappcss.css";
import "../pagecss/QuoteWizard.css";

const API_URL = process.env.REACT_APP_API_URL;

const chip = (active) => ({
  padding: "5px 12px", borderRadius: 999, fontSize: 12.5, fontWeight: 600, cursor: "pointer",
  border: active ? "1.5px solid #4F46E5" : "1px solid #E2E8F0",
  background: active ? "#EEF2FF" : "#fff", color: active ? "#4338CA" : "#475569",
});

// Standard grid header styles — same look as the Quote/Shipment tables.
const TH = { padding: "7px 12px", fontWeight: 700, whiteSpace: "nowrap" };
const THF = { padding: "6px 8px", borderTop: "1px solid #E2E8F0", verticalAlign: "top", background: "#F8FAFC" };
const HEAD_TR = { background: "#F8FAFC", textAlign: "left", color: "#64748B", fontSize: 11, textTransform: "uppercase", letterSpacing: ".04em" };

// Column-filter substring match: empty filter passes; any candidate value can hit.
const hit = (f, ...vals) => {
  const q = (f || "").trim().toLowerCase();
  return !q || vals.some((v) => String(v ?? "").toLowerCase().includes(q));
};

// Uniform compact select (matches the AR dashboard control panel).
const SEL = { padding: "5px 10px", height: 31, width: 150, borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12.5, background: "#fff", color: "#334155" };

export default function FinanceCockpit() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("batches");     // batches | posting | unbilled | all
  const [cat, setCat] = useState("ALL");
  const [batches, setBatches] = useState(null);
  const [queue, setQueue] = useState(null);
  const [unbilled, setUnbilled] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [note, setNote] = useState(null);
  const [colF, setColF] = useState({});                  // per-column grid filters
  const setCol = (k, v) => setColF((o) => ({ ...o, [k]: v }));
  useEffect(() => { setColF({}); }, [tab, cat]);

  // GL post flow state
  const [busyMsg, setBusyMsg] = useState(null);          // blocking overlay text
  const [postedModal, setPostedModal] = useState(null);  // {groupId, invoices, total}
  const [dlBusy, setDlBusy] = useState(null);            // epicorGroupId whose Edit List is downloading
  const [health, setHealth] = useState(null);            // billing job health, loaded on open
  const [healthOpen, setHealthOpen] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [bRes, qRes] = await Promise.all([
        apiFetch(`${API_URL}/api/invoices/batches`),
        apiFetch(`${API_URL}/api/invoices/posting-queue`),
      ]);
      const b = await bRes.json().catch(() => null);
      const q = await qRes.json().catch(() => null);
      if (!bRes.ok) throw new Error(b?.error || `Batches load failed (HTTP ${bRes.status}).`);
      setBatches(Array.isArray(b) ? b : []);
      setQueue(Array.isArray(q) ? q : []);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  const loadUnbilled = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await apiFetch(`${API_URL}/api/invoices/unbilled-shipments`);
      const d = await r.json().catch(() => null);
      if (!r.ok) throw new Error(d?.error || `Load failed (HTTP ${r.status}).`);
      setUnbilled(d);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => { if (tab === "unbilled" && unbilled == null) loadUnbilled(); }, [tab, unbilled, loadUnbilled]);

  const catFilter = useCallback((rows) => {
    const c = CATS[cat]?.cat;
    return c ? (rows || []).filter((b) => b.planCategory === c) : (rows || []);
  }, [cat]);
  // Posted and Closed are both finished. Closed is how a batch that was generated
  // before the nothing-to-bill guard existed gets off this queue: 159 rows of
  // $0.00 stamped ReadyForReview were asking finance to review nothing.
  const isDone = (b) => b.status === "Posted" || b.status === "Closed";
  const activeBatches = useMemo(() => catFilter((batches || []).filter((b) => !isDone(b))), [batches, catFilter]);
  const allBatches = useMemo(() => catFilter(batches || []), [batches, catFilter]);

  // ── Edit List (PDF; 202 = still generating → retry) ────────────────────────
  const downloadEditList = async (groupId) => {
    setNote(null); setError(null); setDlBusy(groupId);
    try {
      for (let attempt = 0; attempt < 3; attempt++) {
        const r = await apiFetch(`${API_URL}/api/invoices/groups/${encodeURIComponent(groupId)}/edit-list`);
        if (r.status === 202) {
          setNote(`Edit List for ${groupId} is still generating — retrying…`);
          await new Promise((res) => setTimeout(res, 2500));
          continue;
        }
        if (!r.ok) {
          const j = await r.json().catch(() => null);
          throw new Error(j?.error || j?.message || `Edit List failed (HTTP ${r.status}) — are there unposted invoices in the group?`);
        }
        const blob = await r.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = `AR_Edit_List_${groupId}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 15000);
        setNote(`Edit List ${groupId} downloaded — check debits = credits before posting.`);
        return;
      }
      throw new Error("Edit List is still generating — try again in a moment.");
    } catch (e) { setError(e.message); }
    finally { setDlBusy(null); }
  };

  // ── GL post flow ───────────────────────────────────────────────────────────
  const postJson = async (path) => {
    const r = await apiFetch(`${API_URL}${path}`, { method: "POST" });
    const d = await r.json().catch(() => null);
    return { ok: r.ok, d };
  };

  const reconcileAndShow = async (batch) => {
    setBusyMsg(`Epicor is posting group ${batch.epicorGroupId} — verifying…`);
    const MAX = 30;
    for (let i = 0; i < MAX; i++) {
      const { d } = await postJson(`/api/invoices/batches/${batch.id}/reconcile`);
      if (d?.allPosted || d?.batchMarkedPosted || d?.batchStatus === "Posted") {
        setBusyMsg(null);
        setPostedModal({
          groupId: batch.epicorGroupId,
          invoices: d?.invoices || [],
          total: (d?.invoices || []).reduce((t, x) => t + (x.amount || 0), 0),
        });
        loadAll();
        return;
      }
      setBusyMsg(`Epicor is posting group ${batch.epicorGroupId} — verifying… (${i + 1}/${MAX})`);
      await new Promise((res) => setTimeout(res, 4000));
    }
    setBusyMsg(null);
    setNote("Epicor is still posting; it will show Posted shortly (or after the hourly reconcile).");
    loadAll();
  };

  const postGroup = async (batch) => {
    setError(null); setNote(null);
    const gid = batch.epicorGroupId;
    try {
      // 1. Validate-only preview — never touches the ledger.
      setBusyMsg(`Validating group ${gid}…`);
      const { d: pre } = await postJson(`/api/invoices/groups/${encodeURIComponent(gid)}/post?validateOnly=true`);
      setBusyMsg(null);

      // 2. Self-heal: local AwaitingPost is just a lagging mirror.
      const msg = pre?.message || "";
      // UI-FIN-011: no bare "posting" — the hold refusal ("… resolve before posting.") must be shown, not reconciled.
      if (!pre?.canPost && (/already posted|not found|being posted/i.test(msg) || pre?.lockStatus === "Posted")) {
        await reconcileAndShow(batch);
        return;
      }
      if (!pre?.canPost) throw new Error(pre?.message || pre?.warnings?.[0] || pre?.error || "Group cannot be posted.");

      // 3. Hard confirm with the REAL amount.
      if (!window.confirm(
        `Post ${money(pre.totalAmount ?? batch.totalAmount)} to the GL for group ${gid}?\n\n` +
        `⚠ Make sure the 📄 Edit List shows debits = credits first.\n` +
        `This commits the invoices to Epicor and CANNOT be undone.`)) return;

      // 4. Real post.
      setBusyMsg(`Posting group ${gid} to the GL…`);
      const { d: real } = await postJson(`/api/invoices/groups/${encodeURIComponent(gid)}/post`);
      if (!real?.submitted && !real?.simulated) {
        setBusyMsg(null);
        throw new Error(real?.message || real?.error || "Post was not submitted.");
      }
      if (real?.simulated) {
        setBusyMsg(null);
        setNote(real?.message || "SIMULATED — InvoicePosting:Enabled is off; nothing was submitted to the GL.");
        return;
      }

      // 5. Epicor posts async — head start, then poll reconcile.
      for (let s = 8; s > 0; s--) {
        setBusyMsg(`Posted to the Epicor agent — waiting ${s}s before verifying…`);
        await new Promise((res) => setTimeout(res, 1000));
      }
      await reconcileAndShow(batch);
    } catch (e) {
      setBusyMsg(null);
      setError(e.message);
    }
  };

  const markPosted = async (batch) => {
    if (!window.confirm(`Record that finance posted batch ${batch.batchCode} in Epicor?\n(This records the fact — it does not post.)`)) return;
    setError(null);
    const { ok, d } = await postJson(`/api/invoices/batches/${batch.id}/mark-posted`);
    if (!ok) setError(d?.error || d?.message || "Mark-posted failed.");
    else { setNote(d?.message || `Batch ${batch.batchCode} marked posted.`); loadAll(); }
  };

  // ── renderers ──────────────────────────────────────────────────────────────
  // NOTE: these renderers are invoked as plain functions (BatchRows({rows})), not
  // as <BatchRows/> components — a nested component type is recreated every parent
  // render, which would remount the tree and drop focus from the filter inputs.
  function BatchRows({ rows }) {
    if (!rows.length) return <div style={{ textAlign: "center", padding: 28, color: "#64748B" }}>{emptyForCat(cat)}</div>;
    const vis = rows.filter((b) =>
      hit(colF.batch, b.batchCode, b.id, fmtDate(b.createdAt), b.postedBy)
      && hit(colF.plan, b.planCategory)
      && hit(colF.group, b.epicorGroupId)
      && hit(colF.inv, b.invoiceCount)
      && hit(colF.total, b.totalAmount, money(b.totalAmount))
      && hit(colF.status, b.status)
      && hit(colF.posting, b.status === "Posted" ? "Posted" : b.agingLabel, b.heldCount > 0 ? `held ${b.heldCount}` : "")
    );
    return (
      <table className="dyn-table-dense fin-table" style={{ width: "100%", tableLayout: "fixed", borderCollapse: "collapse", fontSize: 13 }}>
        <colgroup>
          <col style={{ width: "18%" }} />
          <col style={{ width: "6%" }} />
          <col style={{ width: "13%" }} />
          <col style={{ width: "7%" }} />
          <col style={{ width: "9%" }} />
          <col style={{ width: "12%" }} />
          <col style={{ width: "15%" }} />
          <col style={{ width: "20%" }} />
        </colgroup>
        <thead>
          <tr style={HEAD_TR}>
            <th style={TH}>Batch</th>
            <th style={TH}>Plan</th>
            <th style={TH}>Epicor Group</th>
            <th style={TH}>Invoices</th>
            <th style={TH}>Total</th>
            <th style={TH}>Status</th>
            <th style={TH}>Posting</th>
            <th style={TH}>Actions</th>
          </tr>
          <tr>
            {["batch", "plan", "group", "inv", "total", "status", "posting"].map((k) => (
              <th key={k} style={THF}><Form.Control size="sm" placeholder="Filter…" value={colF[k] || ""} onChange={(e) => setCol(k, e.target.value)} /></th>
            ))}
            <th style={THF} />
          </tr>
        </thead>
        <tbody>
          {!vis.length && <tr><td colSpan={8} style={{ textAlign: "center", padding: 24, color: "#94A3B8" }}>No rows match the column filters.</td></tr>}
          {vis.map((b) => (
            <tr key={b.id}>
              <td data-label="Batch">
                <span style={{ fontWeight: 700, color: "#4338CA", cursor: "pointer" }}
                  onClick={() => navigate(`/finance/batch/${encodeURIComponent(b.batchCode)}`, { state: { batch: b } })}>
                  {b.batchCode}
                </span>
                <div style={{ fontSize: 11, color: "#94A3B8" }}>
                  #{b.id} · {fmtDate(b.createdAt)}
                  {b.postedBy && <span title="Who submitted the post — Epicor itself only logs the ShuklaWebApp service account"> · 👤 posted by {b.postedBy}</span>}
                </div>
              </td>
              <td data-label="Plan"><Badge bg="light" text="dark" style={{ border: "1px solid #E2E8F0" }}>{b.planCategory}</Badge></td>
              <td data-label="Group" style={{ fontFamily: "monospace", fontSize: 12 }}>{b.epicorGroupId || "—"}</td>
              <td data-label="Inv">{b.invoiceCount}</td>
              <td data-label="Total" style={{ fontWeight: 600 }}>{money(b.totalAmount)}</td>
              <td data-label="Status"><Badge bg={STATUS_PILL[b.status] || "secondary"}>{b.status}</Badge></td>
              <td data-label="Posting">
                <span style={{ color: AGING_TONE[b.agingBucket] || "#64748B", fontWeight: b.agingBucket === "Overdue" ? 700 : 500 }}>
                  {b.status === "Posted" ? "Posted" : b.agingLabel || "—"}
                </span>
                {b.heldCount > 0 && <Badge bg="warning" text="dark" style={{ marginLeft: 6 }}>🔒 {b.heldCount} held</Badge>}
              </td>
              <td data-label="" style={{ whiteSpace: "nowrap" }}>
                <Button size="sm" variant="outline-primary" style={{ marginRight: 6 }}
                  title="Open batch review"
                  onClick={() => navigate(`/finance/batch/${encodeURIComponent(b.batchCode)}`, { state: { batch: b } })}>
                  <FaFolderOpen />
                </Button>
                {b.epicorGroupId && hasAction("00078", true) && (
                  <>
                    <Button size="sm" variant="outline-secondary" style={{ marginRight: 6 }}
                      disabled={!b.invoiceCount || !!dlBusy}
                      title={!b.invoiceCount
                        ? "No invoices in this batch — nothing to download"
                        : "Download AR Invoice Edit List (PDF) — the pre-post GL proof (debits must equal credits)"}
                      onClick={() => downloadEditList(b.epicorGroupId)}>
                      {dlBusy === b.epicorGroupId ? <Spinner size="sm" animation="border" /> : <FaFileDownload />}
                    </Button>
                    {b.status !== "Posted" && (
                      <Button size="sm" variant="outline-danger" onClick={() => postGroup(b)}>Post group</Button>
                    )}
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  function PostingRows() {
    const rows = queue || [];
    if (!rows.length) return <div style={{ textAlign: "center", padding: 28, color: "#64748B" }}>Nothing awaiting posting.</div>;
    const vis = rows.filter((b) =>
      hit(colF.batch, b.batchCode)
      && hit(colF.plan, b.planCategory)
      && hit(colF.sent, b.allSentAt ? new Date(b.allSentAt).toLocaleString() : "")
      && hit(colF.buffer, fmtDate(b.bufferExpiresAt))
      && hit(colF.aging, b.agingLabel)
      && hit(colF.held, b.heldCount > 0 ? `held ${b.heldCount}` : "")
    );
    return (
      <table className="dyn-table-dense fin-table" style={{ width: "100%", tableLayout: "fixed", borderCollapse: "collapse", fontSize: 13 }}>
        <colgroup>
          <col style={{ width: "14%" }} />
          <col style={{ width: "8%" }} />
          <col style={{ width: "16%" }} />
          <col style={{ width: "12%" }} />
          <col style={{ width: "14%" }} />
          <col style={{ width: "10%" }} />
          <col style={{ width: "26%" }} />
        </colgroup>
        <thead>
          <tr style={HEAD_TR}>
            <th style={TH}>Batch</th>
            <th style={TH}>Plan</th>
            <th style={TH}>Sent At</th>
            <th style={TH}>Buffer Ends</th>
            <th style={TH}>Aging</th>
            <th style={TH}>Held</th>
            <th style={TH}>Actions</th>
          </tr>
          <tr>
            {["batch", "plan", "sent", "buffer", "aging", "held"].map((k) => (
              <th key={k} style={THF}><Form.Control size="sm" placeholder="Filter…" value={colF[k] || ""} onChange={(e) => setCol(k, e.target.value)} /></th>
            ))}
            <th style={THF} />
          </tr>
        </thead>
        <tbody>
          {!vis.length && <tr><td colSpan={7} style={{ textAlign: "center", padding: 24, color: "#94A3B8" }}>No rows match the column filters.</td></tr>}
          {vis.map((b) => (
            <tr key={b.id} style={b.agingBucket === "Overdue" ? { background: "#FEF2F2" } : undefined}>
              <td data-label="Batch">
                <span style={{ fontWeight: 700, color: "#4338CA", cursor: "pointer" }}
                  onClick={() => navigate(`/finance/batch/${encodeURIComponent(b.batchCode)}`, { state: { batch: b } })}>
                  {b.batchCode}
                </span>
              </td>
              <td data-label="Plan">{b.planCategory}</td>
              <td data-label="Sent">{b.allSentAt ? new Date(b.allSentAt).toLocaleString() : "—"}</td>
              <td data-label="Buffer ends">{fmtDate(b.bufferExpiresAt)}</td>
              <td data-label="Aging" style={{ color: AGING_TONE[b.agingBucket] || "#64748B", fontWeight: 600 }}>{b.agingLabel}</td>
              <td data-label="Held">{b.heldCount > 0 ? <Badge bg="warning" text="dark">🔒 {b.heldCount}</Badge> : "—"}</td>
              <td data-label="">
                {hasAction("00078", true) && (
                  <Button size="sm" variant="outline-success" disabled={b.postBlockedByHolds}
                    title={b.postBlockedByHolds ? "resolve holds first" : "Record that finance posted this batch in Epicor"}
                    onClick={() => markPosted(b)}>
                    Mark posted ✓
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  function UnbilledRows() {
    if (!unbilled) return null;
    const rows = unbilled.items || [];
    const vis = rows.filter((s) =>
      hit(colF.pack, s.packNum)
      && hit(colF.order, s.orderNum)
      && hit(colF.customer, s.customerName, s.custID)
      && hit(colF.age, `${s.ageDays}d`)
      && hit(colF.amount, s.orderAmount, money(s.orderAmount))
      && hit(colF.reason, s.reason === "NotOwned" ? "Not owned" : "Owned unbilled", s.reasonDetail)
    );
    return (
      <>
        {rows.length === 0 ? (
          <div style={{ textAlign: "center", padding: 28, color: "#16A34A" }}>✅ No unbilled Shukla shipments — everything that shipped got billed.</div>
        ) : (
          <table className="dyn-table-dense fin-table" style={{ width: "100%", tableLayout: "fixed", borderCollapse: "collapse", fontSize: 13 }}>
            <colgroup>
              <col style={{ width: "10%" }} />
              <col style={{ width: "10%" }} />
              <col style={{ width: "30%" }} />
              <col style={{ width: "8%" }} />
              <col style={{ width: "12%" }} />
              <col style={{ width: "30%" }} />
            </colgroup>
            <thead>
              <tr style={HEAD_TR}>
                <th style={TH}>Pack #</th>
                <th style={TH}>Order #</th>
                <th style={TH}>Customer</th>
                <th style={TH}>Age</th>
                <th style={TH}>Amount</th>
                <th style={TH}>Reason</th>
              </tr>
              <tr>
                {["pack", "order", "customer", "age", "amount", "reason"].map((k) => (
                  <th key={k} style={THF}><Form.Control size="sm" placeholder="Filter…" value={colF[k] || ""} onChange={(e) => setCol(k, e.target.value)} /></th>
                ))}
              </tr>
            </thead>
            <tbody>
              {!vis.length && <tr><td colSpan={6} style={{ textAlign: "center", padding: 24, color: "#94A3B8" }}>No rows match the column filters.</td></tr>}
              {vis.map((s) => (
                <tr key={s.packNum} style={{ background: s.reason === "NotOwned" ? "#FEF2F2" : "#FFFBEB" }}>
                  <td data-label="Pack" style={{ fontWeight: 600 }}>{s.packNum}</td>
                  <td data-label="Order">{s.orderNum}</td>
                  <td data-label="Customer">{s.customerName || s.custID}</td>
                  <td data-label="Age">{s.ageDays}d</td>
                  <td data-label="Amount">{money(s.orderAmount)}</td>
                  <td data-label="Reason">
                    <Badge bg={s.reason === "NotOwned" ? "danger" : "warning"} text={s.reason === "NotOwned" ? undefined : "dark"} title={s.reasonDetail || undefined}>
                      {s.reason === "NotOwned" ? "Not owned" : "Owned · unbilled"}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div style={{ fontSize: 12, color: "#94A3B8", padding: "8px 4px" }}>
          {unbilled.unbilledCount} unbilled · ~{money(unbilled.unbilledOrderAmount)} at risk ·
          notOwned {unbilled.notOwnedCount} · ownedUnbilled {unbilled.ownedButUnbilledCount} ·
          oldest {unbilled.oldestAgeDays}d
        </div>
      </>
    );
  }

  // ── Billing job health ────────────────────────────────────────────────────
  // Loads only when the panel opens. This is the control that REPLACED the empty
  // batch rows: a batch that was never written is indistinguishable from a job
  // that never ran, so absence has to be detectable somewhere that is not the
  // batch list. Here.
  const openHealth = async () => {
    setHealthOpen(true);
    if (health) return;                    // already loaded this session
    try {
      const r = await apiFetch(`${API_URL}/api/job-runs/billing-health`);
      setHealth(r.ok ? await r.json() : { error: "Couldn't read job history." });
    } catch {
      setHealth({ error: "Couldn't reach the server." });
    }
  };

  const tabs = [
    ["batches", `Batches${batches ? ` (${(batches || []).filter((b) => !isDone(b)).length})` : ""}`],
    ["posting", `Posting Queue${queue ? ` (${queue.length})` : ""}`],
    ["unbilled", `Unbilled Shipments${unbilled ? ` (${unbilled.unbilledCount})` : ""}`],
    ["all", "All Batches · 30d"],
  ];

  return (
    <div style={{ display: "flow-root" }}>
    <div className="qw-root content-halo dyn-qw dyn-search-shell">
      <div className="qw-header" style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div className="qw-header-icon"><FaFileInvoiceDollar size={18} /></div>
          <div>
            <div className="qw-header-title">Invoice Program · Finance</div>
            <div className="qw-header-sub">batches · review · posting</div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {/* Job runs are head-office only (the API answers 403 otherwise — SAL-GAP-67). */}
          {isShuklaIt() && (
          <Button className="qw-btn qw-btn-sm"
            title="When the nightly invoice runs last executed"
            style={{ background: "rgba(255,255,255,.18)", color: "#fff", border: "none", fontWeight: 600 }}
            onClick={openHealth}>
            <FaHeartbeat style={{ marginRight: 6 }} /> Job runs
          </Button>
          )}
          <Button className="qw-btn qw-btn-sm" title="Refresh"
            style={{ background: "rgba(255,255,255,.18)", color: "#fff", border: "none", fontWeight: 600 }}
            onClick={() => { setUnbilled(null); loadAll(); if (tab === "unbilled") loadUnbilled(); }}>
            <FaSync />
          </Button>
        </div>
      </div>

      {/* Single control row — the four tabs share the width equally; the Terms
          dropdown (plan filter, batch views only) sits at the right end. */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, flexWrap: "nowrap" }}>
        {tabs.map(([k, label]) => (
          <span key={k} style={{ ...chip(tab === k), flex: 1, textAlign: "center", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} onClick={() => setTab(k)}>{label}</span>
        ))}
        {(tab === "batches" || tab === "all") && (
          <>
            <span style={{ color: "#94A3B8", fontWeight: 600, fontSize: 12.5, whiteSpace: "nowrap" }}>Terms:</span>
            <select value={cat} onChange={(e) => setCat(e.target.value)} style={SEL}>
              {Object.entries(CATS).map(([k, c]) => <option key={k} value={k}>{c.label}</option>)}
            </select>
          </>
        )}
      </div>

      {/* Results scroll internally (sticky column headers) so the page stays put. */}
      <div className="dyn-results-wrap">
      <div className="dyn-results-scroll">
      {note && <Alert variant="success" dismissible onClose={() => setNote(null)} style={{ fontSize: 13 }}>{note}</Alert>}
      {error && <Alert variant="danger" dismissible onClose={() => setError(null)} style={{ fontSize: 13 }}>{error}</Alert>}

      {loading && !batches ? (
        <div style={{ textAlign: "center", padding: 48, color: "#64748B" }}><Spinner animation="border" size="sm" /> Loading…</div>
      ) : (
        <div style={{ border: "1px solid #E2E8F0", borderRadius: 12, background: "#fff" }}>
          {tab === "batches" && BatchRows({ rows: activeBatches })}
          {tab === "all" && BatchRows({ rows: allBatches })}
          {tab === "posting" && PostingRows()}
          {tab === "unbilled" && (loading ? <div style={{ textAlign: "center", padding: 28 }}><Spinner animation="border" size="sm" /></div> : UnbilledRows())}
        </div>
      )}

      <div style={{ fontSize: 11.5, color: "#94A3B8", marginTop: 8 }}>
        {tab === "batches" && `${activeBatches.length} active batch(es)`}
        {tab === "all" && `${allBatches.length} batch(es)`}
        {tab === "posting" && `${(queue || []).length} awaiting posting · most-overdue first`}
      </div>
      </div>
      </div>

      {/* Blocking overlay for the GL post */}
      {busyMsg && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.55)", zIndex: 3000, display: "grid", placeItems: "center" }}>
          <div style={{ background: "#fff", borderRadius: 14, padding: "26px 34px", textAlign: "center", maxWidth: 420 }}>
            <Spinner animation="border" style={{ marginBottom: 12 }} />
            <div style={{ fontWeight: 700, fontSize: 15 }}>{busyMsg}</div>
            <div style={{ fontSize: 12, color: "#94A3B8", marginTop: 6 }}>Do not close this page.</div>
          </div>
        </div>
      )}

      {/* Posted-success modal */}
      {/* ── Billing job health ──────────────────────────────────────────────
          Answers the question the batch list used to answer badly: did the
          nightly runs actually happen? A job that has not run is now visible as
          a red row here rather than as the absence of a row over there. */}
      <Modal show={healthOpen} onHide={() => setHealthOpen(false)} centered size="lg">
        <Modal.Header closeButton>
          <div>
            <Modal.Title style={{ fontSize: 16.5, fontWeight: 700 }}>Nightly invoice runs</Modal.Title>
            <div style={{ fontSize: 12, color: "#94A3B8", marginTop: 2 }}>
              When each plan's generation job last executed
            </div>
          </div>
        </Modal.Header>

        <Modal.Body>
          {!health && <div style={{ fontSize: 13, color: "#64748B" }}><Spinner animation="border" size="sm" /> Checking…</div>}
          {health?.error && <Alert variant="warning" style={{ fontSize: 13 }}>{health.error}</Alert>}

          {health?.jobs && (
            <>
              {health.lateCount > 0 && (
                <Alert variant="danger" style={{ fontSize: 13 }}>
                  <strong>{health.lateCount} of {health.jobs.length} billing jobs haven't run recently.</strong>{" "}
                  Shipments that are ready to bill are not being invoiced while this is true.
                </Alert>
              )}
              {health.healthy && (
                <Alert variant="success" style={{ fontSize: 13 }}>
                  All {health.jobs.length} billing jobs ran within the last {health.staleHours} hours.
                </Alert>
              )}

              <Table size="sm" style={{ fontSize: 13, marginBottom: 4 }}>
                <thead>
                  <tr style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: ".05em", color: "#94A3B8" }}>
                    <th>Plan</th><th>Last run (UTC)</th><th>Age</th><th>Result</th>
                  </tr>
                </thead>
                <tbody>
                  {health.jobs.map((j) => (
                    <tr key={j.jobName} style={{ background: j.late || j.failed ? "#FEF2F2" : undefined }}>
                      <td style={{ fontWeight: 700 }}>{j.plan}</td>
                      <td className="qw-mono" style={{ color: "#64748B" }}>
                        {j.neverRan ? "never" : String(j.lastRunUtc).replace("T", " ").slice(0, 19)}
                      </td>
                      <td className="qw-mono" style={{ color: j.late ? "#DC2626" : "#64748B", fontWeight: j.late ? 700 : 400 }}>
                        {j.neverRan ? "—" : `${j.hoursAgo}h`}
                      </td>
                      <td>
                        {j.neverRan   ? <Badge bg="danger">Never ran</Badge>
                         : j.failed   ? <Badge bg="danger">Failed</Badge>
                         : j.late     ? <Badge bg="danger">Overdue</Badge>
                         : <Badge bg="success">{j.status}</Badge>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              <div style={{ fontSize: 11.5, color: "#94A3B8", lineHeight: 1.5 }}>
                A run that finds nothing to bill is normal and writes no batch — the batch list
                holds only real invoices. This is where you confirm the run happened.
              </div>
            </>
          )}
        </Modal.Body>

        <Modal.Footer><Button onClick={() => setHealthOpen(false)}>Close</Button></Modal.Footer>
      </Modal>

      <Modal show={!!postedModal} onHide={() => setPostedModal(null)} centered size="lg">
        <Modal.Header closeButton style={{ background: "#F0FDF4" }}>
          <Modal.Title style={{ fontSize: 17, color: "#166534" }}>✓ Group {postedModal?.groupId} has been posted</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Table size="sm" style={{ fontSize: 13 }}>
            <thead>
              <tr style={HEAD_TR}>
                <th style={TH}>Invoice #</th>
                <th style={TH}>Customer</th>
                <th style={{ ...TH, textAlign: "right" }}>Amount</th>
                <th style={TH}>Status</th>
              </tr>
            </thead>
            <tbody>
              {(postedModal?.invoices || []).map((x) => (
                <tr key={x.invoiceNum}>
                  <td>{x.invoiceNum}</td><td>{x.customerName}</td>
                  <td style={{ textAlign: "right" }}>{money(x.amount)}</td>
                  <td><Badge bg={x.posted ? "success" : "secondary"}>{x.posted ? "✓ Posted" : "pending"}</Badge></td>
                </tr>
              ))}
            </tbody>
          </Table>
          <div style={{ textAlign: "right", fontWeight: 700 }}>Total {money(postedModal?.total)}</div>
        </Modal.Body>
        <Modal.Footer><Button onClick={() => setPostedModal(null)}>Close</Button></Modal.Footer>
      </Modal>
    </div>
    </div>
  );
}
